import { getInjectMap, COLUMN_SELECTOR, INJECT_ANCHOR_SELECTOR } from "./config.js";
import { fetchDocSafe } from "./fetch-doc.js";
import {
    getByPath,
    isEmptyValue,
    collapse,
    collapseEmptyContainers,
    resolveTargets,
    resolveDataScript,
    parseJsonScript,
    buildIndexUrl,
    findItemByHref,
    bindSearchDelegation,
    ensureTrailingSlash,
    normalizeHref,
} from "./utils.js";

/** Container, die entfernt werden, wenn nach der Injektion nichts übrig bleibt. */
const EMPTY_CONTAINERS = [
    ".weekly__filter",
    ".weekly__group",
    ".weekly__bundle",
    ".weekly__overlay",
];

/**
 * Ermittelt den Container einer Kachel (weeklyColumn oder bundleColumn).
 */
function findColumn(anchorEl) {
    return anchorEl.closest(COLUMN_SELECTOR) || anchorEl.parentElement;
}

/**
 * Entfernt die parallel gerenderte Partner-Kachel.
 *
 * Die Suchergebnisse rendern pro Treffer beide Varianten mit identischem
 * href: eine weeklyColumn (weekly-v2) und eine bundleColumn (bundle-v2).
 * Sobald eine der beiden Daten gefunden hat, ist der Typ des Treffers
 * geklärt und die andere Kachel wird entfernt. Passiert das früh genug,
 * fragt die entfernte Kachel ihre Seite gar nicht erst an.
 */
function removeSiblingTiles(anchorEl, url) {
    const wanted = normalizeHref(url);
    if (!wanted) return;

    const ownId = anchorEl.dataset.injectId;

    for (const other of document.querySelectorAll(INJECT_ANCHOR_SELECTOR)) {
        if (other === anchorEl) continue;
        if (other.dataset.injectId === ownId) continue;
        if (normalizeHref(other.getAttribute("href")) !== wanted) continue;

        findColumn(other)?.remove();
    }
}

/**
 * Schreibt einen Wert gemäß Mapping-Mode in ein Ziel-Element.
 * Gibt zurück, ob dabei sichtbarer Inhalt entstanden ist.
 */
function applyMode(target, mode, value, empty, attr) {
    switch (mode) {
        case "html":
            target.innerHTML = empty ? "" : String(value);
            return !empty;

        case "attr":
            if (!attr) return false;
            if (empty) target.removeAttribute(attr);
            else target.setAttribute(attr, String(value));
            return !empty;

        // Schreibt nichts, steuert nur Collapse bzw. Sichtbarkeit.
        // Nötig für Bereiche mit statischem Text wie ".weekly__date" = "Bundle".
        case "presence":
            return !empty;

        case "text":
        default:
            target.textContent = empty ? "" : String(value);
            return !empty;
    }
}

/**
 * Ermittelt die JSON-Daten zu einer Kachel.
 *
 * 1. Zielseite des Links laden und dort das page-data-Script lesen.
 *    Entfällt komplett, wenn die Map "indexOnly" setzt (bundle-v2): dort
 *    stehen die Daten nie auf der Detailseite.
 * 2. Übersichtsseite laden (z. B. /portal/de/bundles/), die Kachel mit
 *    passendem itemAttr (bundle-v2: data-search-href) finden und deren
 *    <script class="item-data">
 *    auslesen. Aus derselben Kachel kommt auch der endgültige Link
 *    (data-link). Der href-Vergleich läuft über normalizeHref, also ohne
 *    Query und Trailing-Slash.
 *
 * fetchDoc cached pro URL, die Übersicht wird also nur einmal geladen.
 *
 * @returns {{ data: object|null, link: string|null }}
 */
async function resolveItemData(url, anchorEl, mapDef) {
    // Stufe 1 nur, wenn die Map sie überhaupt vorsieht. Ein 404 (oder ein
    // Netzwerkfehler) beendet hier nur Stufe 1, Stufe 2 läuft trotzdem.
    if (!mapDef.indexOnly) {
        const doc = await fetchDocSafe(url);

        if (doc) {
            const direct = parseJsonScript(resolveDataScript(doc, anchorEl, mapDef));
            if (direct) return { data: direct, link: null };
        }
    }

    const indexUrl = mapDef.indexUrl || buildIndexUrl(url, mapDef.indexSegment);
    if (!indexUrl) return { data: null, link: null };

    const indexDoc = await fetchDocSafe(indexUrl);
    if (!indexDoc) return { data: null, link: null };

    const itemEl = findItemByHref(indexDoc, url, mapDef.itemAttr);
    if (!itemEl) return { data: null, link: null };

    const scriptEl = itemEl.querySelector(mapDef.itemJsonSelector);
    const link = mapDef.itemLinkAttr ? itemEl.getAttribute(mapDef.itemLinkAttr) : null;

    return {
        data: parseJsonScript(scriptEl),
        link: link && link.trim() ? link.trim() : null,
    };
}

/**
 * Lädt Inhalte einer Detailseite nach, liest die JSON-Daten aus
 * und injiziert die gemappten Werte in die Kachel (weekly oder bundle).
 */
export async function injectWeeklyContent(anchorEl) {
    if (!anchorEl) return;

    // Die Partner-Kachel kann bereits entfernt worden sein, während dieser
    // Aufruf in der Warteschlange stand. Dann ist nichts mehr zu tun.
    if (!anchorEl.isConnected) return;

    const rawUrl = anchorEl.getAttribute("href");
    if (!rawUrl) return;

    const mapDef = getInjectMap(anchorEl.dataset.injectId);
    if (!mapDef) return;

    // Optional: "/" vor der Query ergänzen, damit ein Klick vor dem
    // Nachladen nicht auf einem Redirect landet.
    const url = mapDef.ensureTrailingSlash ? ensureTrailingSlash(rawUrl) : rawUrl;
    if (url !== rawUrl) anchorEl.setAttribute("href", url);

    const column = findColumn(anchorEl);

    bindSearchDelegation(anchorEl);

    anchorEl.classList.add("is-loading");
    anchorEl.classList.remove("is-error");
    anchorEl.setAttribute("aria-busy", "true");

    try {
        // Gehört die URL zu einem Bundle? Dann übernimmt die bundleColumn
        // und diese Kachel verschwindet, ohne ihre Detailseite anzufragen.
        if (mapDef.deferToMapId) {
            const deferMap = getInjectMap(mapDef.deferToMapId);
            if (deferMap) {
                const probe = await resolveItemData(url, anchorEl, deferMap);
                if (probe.data) {
                    column?.remove();
                    return;
                }
            }
        }

        const { data: pageData, link } = await resolveItemData(url, anchorEl, mapDef);

        if (!pageData) {
            column?.remove();
            return;
        }

        // Endgültigen Link der Kachel aus data-link des Items übernehmen.
        if (link) {
            const finalLink = mapDef.ensureTrailingSlash ? ensureTrailingSlash(link) : link;
            anchorEl.setAttribute("href", finalLink);
            if (column && column !== anchorEl) column.dataset.link = finalLink;
        }

        let anyInjectedNonEmpty = false;

        for (const m of mapDef.mappings) {
            const { from, to, mode = "text", attr, all = false, collapse: collapseSel } = m || {};
            if (!from || !to) continue;

            const value = getByPath(pageData, from);
            const empty = isEmptyValue(value);

            const targets = resolveTargets(anchorEl, to, all);
            for (const target of targets) {
                if (applyMode(target, mode, value, empty, attr)) anyInjectedNonEmpty = true;
            }

            if (collapseSel) collapse(anchorEl, collapseSel, empty);
        }

        // Pflichtfelder der Map prüfen, falls definiert.
        if (Array.isArray(mapDef.requireAny) && mapDef.requireAny.length > 0) {
            const hasRequired = mapDef.requireAny.some(
                (path) => !isEmptyValue(getByPath(pageData, path))
            );
            if (!hasRequired) {
                column?.remove();
                return;
            }
        } else if (!anyInjectedNonEmpty) {
            column?.remove();
            return;
        }

        collapseEmptyContainers(anchorEl, EMPTY_CONTAINERS);

        // Erst jetzt steht fest, dass diese Kachel bleibt: die parallel
        // gerenderte Variante mit derselben URL wird entfernt.
        if (mapDef.removeSiblings) removeSiblingTiles(anchorEl, url);

        anchorEl.classList.add("is-injected");
    } catch (err) {
        console.error(err);
        anchorEl.classList.add("is-error");
    } finally {
        anchorEl.classList.remove("is-loading");
        anchorEl.setAttribute("aria-busy", "false");
    }
}

/** Sprechender Alias, da die Funktion nicht mehr nur Weekly-Kacheln bedient. */
export const injectTileContent = injectWeeklyContent;

// Pro Suchtreffer stehen zwei Kacheln im HTML: `weeklyColumn` (`weekly-v2`) und `bundleColumn` (`bundle-v2`), beide mit derselben URL. Das JS klärt, welche davon passt.
//
// **Die Bundle-Kachel** lädt nie die Detailseite (`indexOnly`). Sie holt `/portal/de/bundles/`, sucht dort das Item mit passendem `data-search-href`, liest dessen `script.item-data` und setzt den `href` aus `data-link`. Kein Item gefunden → die Kachel entfernt sich.
//
// **Die Weekly-Kachel** fragt zuerst dieselbe Bundle-Übersicht (`deferToMapId`). Gibt es dort ein Item, ist der Treffer ein Bundle und sie entfernt sich sofort – deshalb kein 404 mehr. Sonst lädt sie ihre Detailseite und liest `page-data`.
//
// **Wer befüllt wurde, räumt auf** (`removeSiblings`): die Partner-Kachel mit derselben URL wird entfernt. Übrig bleibt genau eine Kachel pro Treffer.
//
// Dazu zwei Kleinigkeiten: `/portal/de/bundles/` wird durch den Cache in `fetchDoc` nur einmal geladen, egal wie viele Kacheln danach fragen, und beim URL-Vergleich sind Query und Trailing-Slash egal.
